// src/components/modals/OrderModificationModal.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, arrayUnion, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';


function OrderModificationModal({ order, onClose, onOrderUpdated }) {
  const { currentUser, userProfile } = useAuth();
  const [currentStatus, setCurrentStatus] = useState(order.currentStatus);
  const [observations, setObservations] = useState(order.observations || '');
  const [selectedDriverId, setSelectedDriverId] = useState(order.driverId || '');
  const [selectedVehicleId, setSelectedVehicleId] = useState(order.vehicleId || '');
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  // New states for dispatch fields
  const [dispatchType, setDispatchType] = useState(order.dispatchType || ''); // 'Local' or 'Provincia'
  const [dispatchDeliveryAddress, setDispatchDeliveryAddress] = useState(order.dispatchDeliveryAddress || order.deliveryAddress || '');
  const [agencyTrackingNumber, setAgencyTrackingNumber] = useState(order.agencyTrackingNumber || '');
  const [agencyName, setAgencyName] = useState(order.agencyName || '');
  const [agencyDestination, setAgencyDestination] = useState(order.agencyDestination || '');
  const [agencyKey, setAgencyKey] = useState(order.agencyKey || '');

  // States for fields only Administrator can modify
  const [clientName, setClientName] = useState(order.clientName || '');
  const [clientEmail, setClientEmail] = useState(order.clientEmail || '');
  const [contactName, setContactName] = useState(order.contactName || '');
  const [contactPhone, setContactPhone] = useState(order.contactPhone || '');
  const [deliveryAddress, setDeliveryAddress] = useState(order.deliveryAddress || '');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(order.expectedDeliveryDate || '');

  // --- ESTADO PARA EL TIPO DE ENTREGA (Parcial/Total/Recojo) ---
  // Este estado mantendrá su valor y se guarda como 'deliveryType' en Firestore.
  // Inicializa con el valor existente en la orden.
  const [deliveryType, setDeliveryType] = useState(order.deliveryType || '');
  // --- FIN ESTADO ---


  const canModifyOrder = userProfile && (userProfile.rol === 'Administrador' || userProfile.rol === 'Atención al Cliente' || userProfile.rol === 'Asesor' || userProfile.rol === 'Almacen');
  const isAdmin = userProfile && userProfile.rol === 'Administrador'; // Flag for administrator

  // Define all possible statuses within the component scope
  const allPossibleStatuses = [
    'En proceso de Compra',
    'En almacén',
    'Programado para despacho',
    'En Ruta',
    'Reprogramado',
    'Entregado Exitosamente',
    'Pedido listo para Recojo',
    'Entregado en Agencia'
  ];

  // Function to get next status options, now correctly scoped
  const getNextStatusOptions = () => {
    if (userProfile && userProfile.rol === 'Administrador') {
      // Admin can choose any status
      return allPossibleStatuses.sort((a, b) => {
        if (a === order.currentStatus) return -1;
        if (b === order.currentStatus) return 1;
        return 0;
      });
    }

    // Logic for other roles (Asesor, Almacen, Atención al Cliente)
    const options = [order.currentStatus];

    switch (order.currentStatus) {
      case 'En proceso de Compra':
        options.push('En almacén');
        break;
      case 'En almacén':
        options.push('Programado para despacho');
        options.push('Pedido listo para Recojo');
        break;
      case 'Programado para despacho':
        options.push('En Ruta');
        break;
      case 'En Ruta':
        if (order.dispatchType === 'Provincia') {
          options.push('Entregado en Agencia');
        } else {
          options.push('Entregado Exitosamente');
        }
        options.push('Reprogramado');
        break;
      case 'Pedido listo para Recojo':
        options.push('Entregado Exitosamente');
        break;
      case 'Entregado en Agencia':
        options.push('Entregado Exitosamente');
        break;
      case 'Reprogramado':
        options.push('En proceso de Compra', 'En almacén', 'Programado para despacho', 'En Ruta', 'Pedido listo para Recojo', 'Entregado en Agencia', 'Entregado Exitosamente');
        break;
      case 'Entregado Exitosamente':
        break;
      default:
        break;
    }
    return [...new Set(options)].sort((a, b) => {
        if (a === order.currentStatus) return -1;
        if (b === order.currentStatus) return 1;
        return 0;
    });
  };

  // Effect to load drivers and vehicles when the modal opens or status changes
  useEffect(() => {
    const loadTransportData = async () => {
      setLoadingData(true);
      setError('');
      try {
        const driversData = await getDocs(collection(db, 'conductores'));
        setAvailableDrivers(driversData.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const vehiclesData = await getDocs(collection(db, 'vehiculos'));
        setAvailableVehicles(vehiclesData.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error al cargar datos de transporte:", err.message);
        setError("No se pudieron cargar conductores o vehículos.");
      } finally {
        setLoadingData(false);
      }
    };

    // Load transport data if current status requires it
    if (canModifyOrder && (currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta' || currentStatus === 'Reprogramado')) {
      loadTransportData();
    } else {
      setLoadingData(false);
    }
  }, [canModifyOrder, currentStatus]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    if (!canModifyOrder) {
      setError('No tienes permiso para modificar este pedido.');
      return;
    }

    if (order.currentStatus === 'Entregado Exitosamente' && !isAdmin) {
        setError('No se puede modificar un pedido que ya ha sido entregado exitosamente (solo Administrador).');
        return;
    }

    // --- VALIDACIÓN PARA deliveryType ---
    // Si el estado es 'En Ruta', el tipo de entrega (Parcial/Total) es obligatorio.
    if (currentStatus === 'En Ruta' && !deliveryType) {
        setError('Para el estado "En Ruta", debe especificar si es un Tipo de Entrega Parcial o Total.');
        return;
    }
    // --- FIN VALIDACIÓN ---

    const orderRef = doc(db, 'pedidos', order.id);
    
    const driverDataForHistory = availableDrivers.find(d => d.id === selectedDriverId);
    const vehicleDataForHistory = availableVehicles.find(v => v.id === selectedVehicleId);

    const updates = {
      currentStatus: currentStatus,
      observations: observations || null,
      updatedAt: serverTimestamp(),
      // --- Guardar deliveryType en el documento principal ---
      // Simplemente usa el valor actual del estado 'deliveryType'.
      // La lógica para establecerlo/mantenerlo se maneja en el onChange del select de estado.
      deliveryType: deliveryType || null, // Guarda null si está vacío, de lo contrario guarda el valor
      // --- Fin guardar ---
      statusHistory: arrayUnion({
        status: currentStatus,
        timestamp: new Date().toISOString(),
        user: currentUser.email,
        // --- Incluir deliveryType en el historial ---
        ...(deliveryType ? { deliveryType: deliveryType } : {}),
        // --- Fin incluir ---
        ...(currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta' ? {
          driverId: selectedDriverId,
          vehicleId: selectedVehicleId,
          driverName: driverDataForHistory?.name || null,
          driverPhone: driverDataForHistory?.phone || null,
          vehiclePlate: vehicleDataForHistory?.plate || null,
          vehicleModel: vehicleDataForHistory?.model || null,
          dispatchType: dispatchType || null,
          dispatchDeliveryAddress: dispatchDeliveryAddress || null,
        } : {}),
        ...(currentStatus === 'Entregado en Agencia' ? {
          agencyTrackingNumber: agencyTrackingNumber || null,
          agencyName: agencyName || null,
          agencyDestination: agencyDestination || null,
          agencyKey: agencyKey || null,
        } : {})
      })
    };

    // Actualizar campos que solo el Administrador puede modificar
    if (isAdmin) {
        updates.clientName = clientName;
        updates.clientEmail = clientEmail;
        updates.contactName = contactName;
        updates.contactPhone = contactPhone;
        updates.deliveryAddress = deliveryAddress;
        updates.expectedDeliveryDate = expectedDeliveryDate;
    }

    // Conditional logic for fields in the main document
    if (currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta') {
      if (!selectedDriverId || !selectedVehicleId || !dispatchType || !dispatchDeliveryAddress) {
        setError('Conductor, vehículo, tipo de despacho y dirección de despacho son obligatorios para este estado.');
        return;
      }
      updates.driverId = selectedDriverId;
      updates.vehicleId = selectedVehicleId;
      updates.dispatchType = dispatchType;
      updates.dispatchDeliveryAddress = dispatchDeliveryAddress.toUpperCase();
      // Also save to the main document for PublicTracking to read
      updates.driverName = driverDataForHistory?.name || null;
      updates.driverPhone = driverDataForHistory?.phone || null;
      updates.vehiclePlate = vehicleDataForHistory?.plate || null;
      updates.vehicleModel = vehicleDataForHistory?.model || null;

    } else if (currentStatus === 'Entregado en Agencia') {
        if (!agencyTrackingNumber || !agencyName || !agencyDestination || !agencyKey) {
            setError('Número de tracking, nombre de agencia, destino y clave son obligatorios para este estado.');
            return;
        }
        updates.agencyTrackingNumber = agencyTrackingNumber.toUpperCase();
        updates.agencyName = agencyName.toUpperCase();
        updates.agencyDestination = agencyDestination.toUpperCase();
        updates.agencyKey = agencyKey.toUpperCase();
        // Clear transport fields if the status is agency
        updates.driverId = null;
        updates.vehicleId = null;
        updates.dispatchType = null;
        updates.dispatchDeliveryAddress = null;
        updates.driverName = null;
        updates.driverPhone = null;
        updates.vehiclePlate = null;
        updates.vehicleModel = null;
    }
    else {
      // Clear all transport and agency fields if the status does not require them
      updates.driverId = null;
      updates.vehicleId = null;
      updates.dispatchType = null;
      updates.dispatchDeliveryAddress = null;
      updates.driverName = null;
      updates.driverPhone = null;
      updates.vehiclePlate = null;
      updates.vehicleModel = null;
      updates.agencyTrackingNumber = null;
      updates.agencyName = null;
      updates.agencyDestination = null;
      updates.agencyKey = null;
    }

    // --- LOG PARA DEPURACIÓN ---
    console.log("Valor de deliveryType antes de la actualización:", deliveryType);
    console.log("Objeto 'updates' antes de la actualización:", updates);
    // --- FIN LOG ---

    try {
      await updateDoc(orderRef, updates);
      alert('Pedido actualizado exitosamente!'); // Replace with custom modal in production
      onOrderUpdated();
      onClose();
    } catch (err) {
      console.error("Error al actualizar pedido:", err.message);
      setError("No se pudo actualizar el pedido: " + err.message);
    }
  };

  if (!canModifyOrder) {
    return null; // Or render an access denied message if preferred
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h2 style={{ textAlign: 'center', color: 'var(--secondary-blue)' }}>Modificar Pedido: {order.orderNumber}</h2>
        <p style={{ textAlign: 'center', color: '#666' }}>Cliente: {order.clientName}</p>
        <p style={{ textAlign: 'center', color: '#666' }}>Contacto: {order.contactName} - Teléfono: {order.contactPhone}</p>
        <p style={{ textAlign: 'center', color: '#666' }}>Dirección Original: {order.deliveryAddress}</p>

        {error && <p className="login-error">{error}</p>}

        <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
          {/* Non-modifiable fields */}
          <div className="login-form-group">
            <label>Número de Pedido:</label>
            <input type="text" value={order.orderNumber} disabled style={{ backgroundColor: '#f0f0f0' }} />
          </div>

          {/* Campos editables solo por Administrador */}
          <div className="login-form-group">
            <label htmlFor="clientName">Cliente:</label>
            <input
              type="text"
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="clientEmail">Correo Electrónico:</label>
            <input
              type="email"
              id="clientEmail"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="contactName">Contacto:</label>
            <input
              type="text"
              id="contactName"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="contactPhone">Teléfono Contacto:</label>
            <input
              type="text"
              id="contactPhone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="deliveryAddress">Dirección de Entrega:</label>
            <input
              type="text"
              id="deliveryAddress"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>
          <div className="login-form-group">
            <label htmlFor="expectedDeliveryDate">Fecha Prevista Entrega:</label>
            <input
              type="date"
              id="expectedDeliveryDate"
              value={expectedDeliveryDate}
              onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              disabled={!isAdmin} // Deshabilitado si no es Admin
              style={{ backgroundColor: isAdmin ? 'white' : '#f0f0f0' }}
            />
          </div>


          {/* Current Status Field */}
          <div className="login-form-group">
            <label htmlFor="status">Estado Actual:</label>
            <select
              id="status"
              value={currentStatus}
              onChange={(e) => {
                const newStatus = e.target.value;
                setCurrentStatus(newStatus);
                // Clear transport and agency fields when changing status if they don't apply
                if (newStatus !== 'Programado para despacho' && newStatus !== 'En Ruta') {
                  setSelectedDriverId('');
                  setSelectedVehicleId('');
                  setDispatchType('');
                  setDispatchDeliveryAddress('');
                }
                if (newStatus !== 'Entregado en Agencia') {
                  setAgencyTrackingNumber('');
                  setAgencyName('');
                  setAgencyDestination('');
                  setAgencyKey('');
                }

                // --- REVISED LOGIC FOR deliveryType based on newStatus ---
                if (newStatus === 'Pedido listo para Recojo') {
                  setDeliveryType('Recojo'); // Explicitly set to 'Recojo'
                } else if (newStatus === 'En Ruta') {
                  // If changing to 'En Ruta', and deliveryType was 'Recojo', it should now be cleared
                  // for the user to select 'Parcial' or 'Total'.
                  if (deliveryType === 'Recojo') {
                    setDeliveryType(''); // Clear if it was 'Recojo' and now going 'En Ruta'
                  }
                  // Otherwise, if it was already 'Parcial' or 'Total', keep its value.
                  // If it was empty, it stays empty, waiting for user selection.
                } else {
                  // If changing to any other status (e.g., 'Entregado Exitosamente', 'En almacén', etc.)
                  // We should NOT clear deliveryType if it already has a value (Parcial, Total, Recojo).
                  // It should persist.
                  // Only clear it if it's currently empty or not one of the valid types.
                  if (!['Parcial', 'Total', 'Recojo'].includes(deliveryType)) {
                    setDeliveryType(''); // Only clear if it's not a valid persisted type
                  }
                }
                // --- END REVISED LOGIC ---
              }}
              style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
            >
              {getNextStatusOptions().map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* --- CAMPO PARA TIPO DE ENTREGA (Parcial/Total/Recojo) --- */}
          {/* Se renderiza si el estado actual es 'En Ruta' O si ya tiene un valor (para persistencia) */}
          {(currentStatus === 'En Ruta' || currentStatus === 'Pedido listo para Recojo' || deliveryType) && (
            <div className="login-form-group">
              <label htmlFor="deliveryType">Tipo de Entrega:</label>
              <select
                id="deliveryType"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                // Deshabilitado si el estado NO es 'En Ruta' (ya que 'Recojo' se establece automáticamente o se mantiene)
                disabled={currentStatus !== 'En Ruta'}
                // Requerido solo si el estado es 'En Ruta'
                required={currentStatus === 'En Ruta'}
              >
                <option value="">Selecciona tipo de entrega</option>
                <option value="Parcial">Parcial</option>
                <option value="Total">Total</option>
                {/* Option for 'Recojo' is only shown if it's the current type or status is 'Pedido listo para Recojo' */}
                {(deliveryType === 'Recojo' || currentStatus === 'Pedido listo para Recojo') && (
                  <option value="Recojo">Recojo</option>
                )}
              </select>
            </div>
          )}
          {/* --- FIN CAMPO --- */}


          {/* Observations (visible in certain states) */}
          {(currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta' || currentStatus === 'Reprogramado') && (
            <div className="login-form-group">
              <label htmlFor="observations">Observaciones:</label>
              <textarea
                id="observations"
                placeholder="Añade observaciones sobre la entrega..."
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows="3"
                style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
              ></textarea>
            </div>
          )}

          {/* Dispatch Type, Driver, Vehicle, and Dispatch Address Selection */}
          {(currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta') && (
            <div style={{ border: '1px solid #eee', padding: '15px', borderRadius: '8px', backgroundColor: 'var(--light-blue)' }}>
              <h4 style={{ color: 'var(--primary-red)', marginTop: '0' }}>Asignar Transporte</h4>
              <div className="login-form-group">
                <label htmlFor="dispatchType">Tipo de Despacho:</label>
                <select
                  id="dispatchType"
                  value={dispatchType}
                  onChange={(e) => setDispatchType(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required={currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta'}
                >
                  <option value="">Selecciona tipo</option>
                  <option value="Local">Local (Lima)</option>
                  <option value="Provincia">Provincia</option>
                </select>
              </div>

              <div className="login-form-group">
                <label htmlFor="dispatchDeliveryAddress">Dirección de Despacho:</label>
                <input
                  type="text"
                  id="dispatchDeliveryAddress"
                  placeholder="Dirección específica para este despacho"
                  value={dispatchDeliveryAddress}
                  onChange={(e) => setDispatchDeliveryAddress(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required={currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta'}
                />
              </div>

              {loadingData ? (
                <p>Cargando conductores y vehículos...</p>
              ) : (
                <>
                  <div className="login-form-group">
                    <label htmlFor="driver">Conductor:</label>
                    <select
                      id="driver"
                      value={selectedDriverId}
                      onChange={(e) => setSelectedDriverId(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      required={currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta'}
                    >
                      <option value="">Selecciona un conductor</option>
                      {availableDrivers.map(driver => (
                        <option key={driver.id} value={driver.id}>{driver.name} ({driver.phone})</option>
                      ))}
                    </select>
                  </div>
                  <div className="login-form-group">
                    <label htmlFor="vehicle">Vehículo:</label>
                    <select
                      id="vehicle"
                      value={selectedVehicleId}
                      onChange={(e) => setSelectedVehicleId(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                      required={currentStatus === 'Programado para despacho' || currentStatus === 'En Ruta'}
                    >
                      <option value="">Selecciona un vehículo</option>
                      {availableVehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>{vehicle.plate} ({vehicle.model})</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Fields for Delivered at Agency */}
          {currentStatus === 'Entregado en Agencia' && (
            <div style={{ border: '1px solid #eee', padding: '15px', borderRadius: '8px', backgroundColor: 'var(--light-blue)' }}>
              <h4 style={{ color: 'var(--primary-red)', marginTop: '0' }}>Detalles de Agencia</h4>
              <div className="login-form-group">
                <label htmlFor="agencyTrackingNumber">Número de Tracking de Agencia:</label>
                <input
                  type="text"
                  id="agencyTrackingNumber"
                  placeholder="Número de tracking"
                  value={agencyTrackingNumber}
                  onChange={(e) => setAgencyTrackingNumber(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required
                />
              </div>
              <div className="login-form-group">
                <label htmlFor="agencyName">Nombre de la Agencia:</label>
                <input
                  type="text"
                  id="agencyName"
                  placeholder="Nombre de la agencia de envío"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required
                />
              </div>
              <div className="login-form-group">
                <label htmlFor="agencyDestination">Destino:</label>
                <input
                  type="text"
                  id="agencyDestination"
                  placeholder="Destino final en provincia"
                  value={agencyDestination}
                  onChange={(e) => setAgencyDestination(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required
                />
              </div>
              <div className="login-form-group">
                <label htmlFor="agencyKey">Clave:</label>
                <input
                  type="text"
                  id="agencyKey"
                  placeholder="Clave de recojo (si aplica)"
                  value={agencyKey}
                  onChange={(e) => setAgencyKey(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  required
                />
              </div>
            </div>
          )}


          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
            <button
              type="submit"
              style={{
                backgroundColor: 'var(--secondary-blue)',
                color: 'white',
                flex: 1,
                maxWidth: '150px',
                fontWeight: 'bold',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                padding: '10px 20px',
                transition: 'background-color 0.3s ease, transform 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              Actualizar Pedido
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: 'var(--primary-red)',
                color: 'white',
                flex: 1,
                maxWidth: '150px',
                fontWeight: 'bold',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                padding: '10px 20px',
                transition: 'background-color 0.3s ease, transform 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '30px',
  borderRadius: '10px',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  width: '90%',
  maxWidth: '550px',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
};

export default OrderModificationModal;